import { useState, useEffect, useCallback } from "react";
import { Clock, AlertTriangle, CheckSquare, X, ChevronRight, Bell, Sparkles } from "lucide-react";
import { api } from "../../lib/api";

function formatDateTimeFriendly(dueDate, dueTime) {
  if (!dueDate) return "No deadline";
  try {
    const today = new Date().toISOString().split("T")[0];
    let dateLabel = dueDate;
    if (dueDate === today) {
      dateLabel = "Today";
    } else {
      const d = new Date(dueDate);
      if (!isNaN(d.getTime())) {
        dateLabel = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
      }
    }

    if (dueTime) {
      return `${dateLabel} at ${dueTime}`;
    }
    return dateLabel;
  } catch {
    return dueDate;
  }
}

export default function TaskReminderWidget({ user, onOpenTask }) {
  const [reminders, setReminders] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("dismissed_task_reminders") || "[]");
    } catch {
      return [];
    }
  });

  const loadReminders = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const res = await api("get", "/tasks/reminders");
      if (Array.isArray(res.data)) {
        setReminders(res.data);
      }
    } catch (err) {
      // Silently fail if offline or network hiccup
    }
  }, [user]);

  useEffect(() => {
    loadReminders();
    const interval = setInterval(loadReminders, 25000);
    return () => clearInterval(interval);
  }, [loadReminders]);

  // Filter out dismissed tasks and overdue tasks (do not show overdue reminders as popups)
  const todayStr = new Date().toISOString().split("T")[0];
  const visibleReminders = reminders.filter(
    (r) =>
      !dismissedIds.includes(r.task_id) &&
      r.status !== "Overdue" &&
      r.type !== "overdue" &&
      (!r.due_date || r.due_date >= todayStr)
  );

  if (visibleReminders.length === 0) return null;

  // Active reminder to show
  const activeReminder = visibleReminders[currentIndex % visibleReminders.length] || visibleReminders[0];

  const handleDismiss = (taskId) => {
    const updated = [...dismissedIds, taskId];
    setDismissedIds(updated);
    try {
      sessionStorage.setItem("dismissed_task_reminders", JSON.stringify(updated));
    } catch {}
  };

  const isOverdue = activeReminder.status === "Overdue" || activeReminder.type === "overdue";
  const isToday = activeReminder.type === "today";

  const getBadgeColor = () => {
    if (isOverdue) return { bg: "#fef2f2", border: "#fecaca", text: "#b91c1c", icon: AlertTriangle };
    if (isToday) return { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8", icon: Clock };
    return { bg: "#f8fafc", border: "#e2e8f0", text: "#334155", icon: CheckSquare };
  };

  const styleConfig = getBadgeColor();
  const IconComponent = styleConfig.icon;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 9999,
        maxWidth: 340,
        width: "calc(100vw - 48px)",
        background: "#ffffff",
        borderRadius: 10,
        border: `1px solid ${styleConfig.border}`,
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)",
        overflow: "hidden",
        animation: "slideInUp 0.25s ease-out",
      }}
      data-testid="task-floating-reminder"
    >
      {/* Top Banner */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: styleConfig.bg,
          borderBottom: `1px solid ${styleConfig.border}`,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <IconComponent size={14} color={styleConfig.text} />
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: styleConfig.text }}>
            {isOverdue ? "Task Overdue" : isToday ? "Task Due Today" : "Assigned Task"}
          </span>
          {visibleReminders.length > 1 && (
            <span style={{ fontSize: 10, color: "#64748b", background: "#ffffff", padding: "1px 5px", borderRadius: 10, border: "1px solid #cbd5e1" }}>
              {(currentIndex % visibleReminders.length) + 1} of {visibleReminders.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => handleDismiss(activeReminder.task_id)}
          style={{
            background: "transparent",
            border: 0,
            cursor: "pointer",
            color: "#64748b",
            display: "flex",
            alignItems: "center",
            padding: 2,
          }}
          title="Dismiss reminder"
          data-testid="dismiss-reminder-btn"
        >
          <X size={14} />
        </button>
      </div>

      {/* Main Content */}
      <div style={{ padding: "12px 14px" }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--navy)", marginBottom: 4, lineHeight: 1.3 }}>
          {activeReminder.title}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#64748b", marginBottom: 10 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontWeight: 500, color: isOverdue ? "#dc2626" : "#0f172a" }}>
            <Clock size={12} />
            {formatDateTimeFriendly(activeReminder.due_date, activeReminder.due_time)}
          </span>
          <span>·</span>
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              padding: "1px 6px",
              borderRadius: 4,
              background:
                activeReminder.priority === "Urgent"
                  ? "#fee2e2"
                  : activeReminder.priority === "High"
                  ? "#fef3c7"
                  : "#f1f5f9",
              color:
                activeReminder.priority === "Urgent"
                  ? "#991b1b"
                  : activeReminder.priority === "High"
                  ? "#92400e"
                  : "#334155",
            }}
          >
            {activeReminder.priority}
          </span>
        </div>

        {/* Footer Actions */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {visibleReminders.length > 1 ? (
            <button
              type="button"
              onClick={() => setCurrentIndex((prev) => (prev + 1) % visibleReminders.length)}
              style={{
                background: "transparent",
                border: 0,
                color: "#2563eb",
                fontSize: 11.5,
                fontWeight: 500,
                cursor: "pointer",
                padding: 0,
              }}
            >
              Next ({visibleReminders.length - 1} more)
            </button>
          ) : (
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              By {activeReminder.assigned_by_name || "Admin"}
            </span>
          )}

          <button
            type="button"
            className="primary-btn compact"
            onClick={() => {
              handleDismiss(activeReminder.task_id);
              if (onOpenTask) onOpenTask(activeReminder.task_id);
            }}
            style={{ height: 28, fontSize: 11.5, padding: "0 10px", display: "inline-flex", alignItems: "center", gap: 4 }}
            data-testid="open-task-reminder-btn"
          >
            Open Task <ChevronRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
